import { useEffect, useRef } from 'react';
import { Button, Center, Column, Row, Spinner, Typography } from '@tempmee/react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { SolidExclamationCircleIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { firePermPostJobJobDescriptionSubmitted } from '~/posthogs/events';
import {
  AutoSaveStatus,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import { JobDescriptionForm } from '../JobDetailsForm/components/JobDescription';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';

export const JobDescriptionPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const { draft, isLoading } = useDraftJobPost(id);
  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  const jobDescriptionRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.JobDescription]: jobDescriptionRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.ReviewAndPayment,
    triggers: ['jobDescription'],
    onTrackSuccess: (postingId, formData) => {
      firePermPostJobJobDescriptionSubmitted({
        postingId,
        professionType: formData.professionType,
      });
    },
  });
  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  if (isLoading) {
    return (
      <Center style={{ marginBlock: 48 }}>
        <Spinner centered variant="default" size="md" />
      </Center>
    );
  }

  if (isEnforcePostAJobFunnelImprovement) {
    return (
      <>
        <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
        <Column align="center" gap={4}>
          {hasFormErrors && (
            <Row gap={2} align="center" width="800px">
              <SolidExclamationCircleIcon color={colors.error[500]} />
              <Typography as="span" color={colors.error[500]}>
                Oops! Job description must be at least 150 characters.
              </Typography>
            </Row>
          )}
          <FormProvider {...controller}>
            <Column width="800px" className="self-center">
              <InfoSection
                ref={jobDescriptionRef}
                title="Job Description"
                titleFontSize={24}
                titleColor="neutral_600"
              >
                <JobDescriptionForm
                  control={controller.control}
                  errors={controller.formState.errors}
                />
              </InfoSection>
            </Column>
          </FormProvider>
          <Row justify="center" className="mt-6">
            <Button onClick={onSave} isLoading={isPending || isAutoSaveLoading} size="lg">
              Last Step
            </Button>
          </Row>
        </Column>
      </>
    );
  }

  return (
    <>
      <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
      <Column width="60%" className="self-center">
        <Typography as="span" bold size="heading_4" style={{ marginBottom: 14, marginLeft: 24 }}>
          Last Step: Job Description
        </Typography>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <Column gap={4}>
            <InfoSection
              icon="document-solid"
              ref={jobDescriptionRef}
              required
              title="Job Description"
            >
              <JobDescriptionForm
                control={controller.control}
                errors={controller.formState.errors}
              />
            </InfoSection>
          </Column>
        </FormProvider>
        <Row justify="center" className="mt-6">
          <Button onClick={onSave} isLoading={isPending || isAutoSaveLoading} size="lg">
            Next
          </Button>
        </Row>
      </Column>
    </>
  );
};
